import { isHTTPError } from '@afinius/api-client/client';
import { User } from '@afinius/api-client/user/types';
import { toPathPattern } from '@afinius/utilities/url';
import * as Sentry from '@sentry/vue';
import { upperFirst } from 'lodash';
import type { App } from 'vue';
import { Router } from 'vue-router';

import packageJson from '../../package.json';

interface SentryOptions {
  hasSessionRecovery?: boolean;
}

const createBeforeSend =
  ({ hasSessionRecovery = false }: SentryOptions) =>
  (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
    const error = hint.originalException;

    if (!isHTTPError(error)) return event;

    const { status } = error.response;

    if (hasSessionRecovery && status === 401) return null;

    const endpoint = `${error.request.method} ${toPathPattern(error.request.url)}`;

    event.fingerprint = ['http-error', endpoint, String(status)];
    event.tags = { ...event.tags, 'http.status': status, 'http.endpoint': endpoint };

    return event;
  };

export const registerSentry = (app: App, router: Router, options: SentryOptions = {}): void => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  const isProduction = ['production'].includes(import.meta.env.VITE_ENVIRONMENT);

  Sentry.init({
    environment: upperFirst(import.meta.env.VITE_ENVIRONMENT),
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    release: packageJson.version,
    sampleRate: 1,
    tracesSampleRate: isProduction ? 0.1 : 1,
    ignoreErrors: [
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      /Unable to preload CSS for/,
      /Couldn't resolve component/,
      'Invalid call to runtime.sendMessage',
    ],
    beforeSend: createBeforeSend(options),
  });
};

export const setSentryUser = (user: User): void => {
  Sentry.setUser({
    id: user.id,
    username: user.full_name,
    email: user.email,
  });
};

interface SsoFailure {
  reason?: string;
  failure?: string;
  app?: string;
  origin?: string;
  attempt: number;
}

export const reportSsoFailure = ({ reason, failure, app, origin, attempt }: SsoFailure): void => {
  Sentry.captureMessage('Opening an application through SSO failed', {
    level: 'warning',
    tags: {
      'sso.reason': reason ?? 'none',
      'sso.failure': failure ?? 'unknown',
      'sso.app': app ?? 'unknown',
      'sso.origin': origin ?? 'unknown',
      'sso.attempt': attempt,
    },
  });
};
